import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BopsLogo, BopsTitleLogo } from '@Assets';
import GuideContext, { type TocEntry } from './GuideContext';
import {
  BadgeWrapper,
  CardFooter,
  CardHeader,
  CardSubtitle,
  CardTitle,
  ContentGrid,
  FooterLeft,
  FooterLink,
  FooterText,
  LogoWrapper,
  MainCard,
  PageWrapper,
  Sidebar,
  TocLabel,
  TocLink,
  TocLinkBar,
  TocNav,
} from './GuideLayout.styles';

interface GuideLayoutProps {
  title: string;
  subtitle: ReactNode;
  badge: ReactNode;
  /** Override the TOC section label (e.g. pass a fixed Spanish string for Spanish-only guides). */
  tocLabel?: string;
  /** Override the TOC aria-label. */
  tocAriaLabel?: string;
  /** Override the footer guide label. */
  footerLabel?: string;
  /** Override the footer support blurb. */
  footerSupport?: string;
  children: ReactNode;
}

const GuideLogo = () => (
  <LogoWrapper>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <BopsLogo style={{ width: 28, height: 28 }} />
      <BopsTitleLogo style={{ height: 18, width: 'auto' }} />
    </div>
  </LogoWrapper>
);

const GuideLayout = ({
  title,
  subtitle,
  badge,
  tocLabel: tocLabelProp,
  tocAriaLabel: tocAriaLabelProp,
  footerLabel: footerLabelProp,
  footerSupport: footerSupportProp,
  children,
}: GuideLayoutProps) => {
  const { t } = useTranslation();
  const tocLabel = tocLabelProp ?? t('Guides.Footer.tocLabel');
  const tocAriaLabel = tocAriaLabelProp ?? t('Guides.Footer.tocAriaLabel');
  const footerLabel = footerLabelProp ?? t('Guides.Footer.label');
  const footerSupport = footerSupportProp ?? t('Guides.Footer.support');
  const [sections, setSections] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Allow the page to scroll — body has overflow-y: clip globally for the app shell
  useEffect(() => {
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = 'auto';
    return () => {
      document.body.style.overflowY = prev;
    };
  }, []);

  const register = useCallback((entry: TocEntry) => {
    setSections((prev) => {
      const idx = prev.findIndex((s) => s.id === entry.id);
      if (idx === -1) return [...prev, entry];
      if (prev[idx].label === entry.label) return prev;
      const next = [...prev];
      next[idx] = entry;
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  }, []);

  useEffect(() => {
    observerRef.current?.disconnect();
    if (sections.length > 0) {
      setActiveId((prev) => prev ?? sections[0].id);
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const intersecting = entries.filter((e) => e.isIntersecting);
          if (intersecting.length === 0) return;
          intersecting.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(intersecting[0].target.id);
        },
        { rootMargin: '-10% 0px -75% 0px', threshold: 0 },
      );
      sections.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observerRef.current?.observe(el);
      });
    }
    return () => observerRef.current?.disconnect();
  }, [sections]);

  const ctxValue = useMemo(() => ({ register, unregister }), [register, unregister]);

  return (
    <GuideContext.Provider value={ctxValue}>
      <PageWrapper>
        <ContentGrid>
          <Sidebar>
            <GuideLogo />
            <TocLabel>{tocLabel}</TocLabel>
            <TocNav aria-label={tocAriaLabel}>
              {sections.map(({ id, label }) => {
                const isActive = activeId === id;
                return (
                  <TocLink key={id} href={`#${id}`} $active={isActive}>
                    <TocLinkBar $active={isActive} />
                    {label}
                  </TocLink>
                );
              })}
            </TocNav>
          </Sidebar>

          <MainCard>
            <CardHeader>
              <BadgeWrapper>{badge}</BadgeWrapper>
              <CardTitle>{title}</CardTitle>
              <CardSubtitle>{subtitle}</CardSubtitle>
            </CardHeader>

            {children}

            <CardFooter>
              <FooterLeft>
                <BopsLogo style={{ width: 22, height: 22 }} />
                <FooterText>
                  {footerLabel} · {title}
                </FooterText>
              </FooterLeft>
              <FooterText>
                {footerSupport} <FooterLink href="mailto:support@bops.ai">support@bops.ai</FooterLink>
              </FooterText>
            </CardFooter>
          </MainCard>
        </ContentGrid>
      </PageWrapper>
    </GuideContext.Provider>
  );
};

export default GuideLayout;
export type { GuideLayoutProps };
